import type { FC } from 'react'
import { useState } from 'react'

import { Checkbox, LoaderCircle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { sanitizeAndInsertTargetBlank } from '@skytech/utils/html-sanitization'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { commonQueries } from '@/resources/queries'

import adminQueries from '../../queries'
import type { FeatureDetails } from '../../resources'
import adminResources from '../../resources'

export const TermsOfUse: FC<{
  feature: FeatureDetails
  selectedCustomerId: number
  selectedCustomerActivationHistory: FeatureDetails['customerFeatures'][number]['logs'] | undefined
  onGoBack: () => void
  onClose: () => void
}> = ({ selectedCustomerId, feature, selectedCustomerActivationHistory, onClose, onGoBack }) => {
  const queryClient = useQueryClient()
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)

  const { data, isLoading } = useQuery(adminQueries.adminFeatureTermsOfService(feature.id), () =>
    adminResources.getFeatureTermOfService(feature.id)
  )

  const activateFeatureMutation = useMutation(adminResources.activateFeature, {
    onSuccess() {
      queryClient.invalidateQueries(adminQueries.adminFeatures())
      queryClient.refetchQueries(commonQueries.profile())
      onClose()
    },
  })
  const deactivateFeatureMutation = useMutation(adminResources.deactivateFeature, {
    onSuccess() {
      queryClient.invalidateQueries(adminQueries.adminFeatures())
      queryClient.refetchQueries(commonQueries.profile())
      onClose()
    },
  })

  // todo: discuss whether could be returned from the backend
  const isFeatureActivated = selectedCustomerActivationHistory?.[0]?.action === 'activated'

  return (
    <div>
      <div className="max-h-96 overflow-y-auto">
        {isLoading && <LoaderCircle />}
        {!isLoading && data?.termsOfUse ? (
          <div
            dangerouslySetInnerHTML={{ __html: sanitizeAndInsertTargetBlank(data.termsOfUse) }}
          />
        ) : (
          'No terms of use available for this feature.'
        )}
      </div>

      <footer className="mt-8">
        {data?.termsOfUse && (
          <Checkbox
            id="terms"
            checked={isFeatureActivated || isTermsAccepted}
            isDisabled={isFeatureActivated}
            label="I thereby confirm that I have read and accepted the terms & conditions stated above."
            onChange={() => setIsTermsAccepted(!isTermsAccepted)}
          />
        )}

        <div className="mt-3 flex justify-end">
          <Button variant="tertiary" onClick={onClose}>
            <Translation id="Cancel" />
          </Button>

          <Button variant="tertiary" onClick={onGoBack}>
            <Translation id="Go back" />
          </Button>

          {isFeatureActivated && (
            <Button
              variant="primary"
              isLoading={deactivateFeatureMutation.isLoading}
              onClick={() =>
                deactivateFeatureMutation.mutate({
                  featureId: feature.id,
                  customerId: selectedCustomerId,
                })
              }
            >
              <Translation id="Deactivate" />
            </Button>
          )}

          {!isFeatureActivated && (
            <Button
              variant="primary"
              disabled={!isTermsAccepted && Boolean(data?.termsOfUse)}
              isLoading={activateFeatureMutation.isLoading}
              onClick={() =>
                activateFeatureMutation.mutate({
                  featureId: feature.id,
                  customerId: selectedCustomerId,
                })
              }
            >
              <Translation id="Activate" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  )
}
